import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../Component/Sidebar/Sidebar";
import "./TransactionTableDashboard.css";
import { FaSearch } from "react-icons/fa";

const API_URL = process.env.REACT_APP_API_URL;

const idr = (n) =>
  typeof n === "number"
    ? n.toLocaleString("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0,
      })
    : n;

const toDateStr = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

const pick = (obj, keys) => keys.find((k) => obj?.[k] !== undefined);

const normalizeItem = (t) => {
  if (!t || typeof t !== "object") return null;

  const tanggalKey = pick(t, ["tanggal", "date", "createdAt"]);
  const nominalKey = pick(t, ["nominal", "amount", "total"]);
  const jenisKey = pick(t, ["jenis_kebutuhan", "jenis", "type", "category_2"]);
  const kategoriKey = pick(t, [
    "kategori_pengeluaran",
    "kategori",
    "category",
    "category_1",
  ]);
  const deskripsiKey = pick(t, ["deskripsi", "description", "keterangan"]);

  return {
    _id: t._id,
    tanggal: toDateStr(t[tanggalKey]),
    nominal: Number(t[nominalKey] ?? 0),
    jenis_kebutuhan: t[jenisKey] ?? "",
    kategori_pengeluaran: t[kategoriKey] ?? "",
    deskripsi: t[deskripsiKey] ?? "",
  };
};

const normalizeList = (raw) => {
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.transactions)
    ? raw.transactions
    : Array.isArray(raw?.data)
    ? raw.data
    : [];
  return list.map(normalizeItem).filter(Boolean);
};

export default function Transactiontable({ transactions, data, standalone = false }) {
  const [fetched, setFetched] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState("");
  const [filterCategory, setFilterCategory] = useState("Semua");
  const [filterJenis, setFilterJenis] = useState("Semua");

  useEffect(() => {
    if (Array.isArray(transactions) || Array.isArray(data)) return;
    const fetchTransaction = async () => {
      try {
        setError("");
        const res = await fetch(API_URL, {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        const raw = await res.json();
        if (!res.ok) throw new Error(raw?.message || "Gagal memuat transaksi");
        const norm = normalizeList(raw);
        setFetched(norm);
      } catch (e) {
        console.error("Error fetching:", e);
        setError(e?.message || "Terjadi kesalahan saat memuat data");
      }
    };
    fetchTransaction();
  }, [transactions, data]);

  const source = useMemo(() => {
    const fromProps = normalizeList(transactions ?? data ?? []);
    return fromProps.length ? fromProps : fetched;
  }, [transactions, data, fetched]);

  const uniqueCategories = useMemo(
    () => ["Semua", ...new Set(source.map((t) => t.kategori_pengeluaran).filter(Boolean))],
    [source]
  );
  const uniqueJenis = useMemo(
    () => ["Semua", ...new Set(source.map((t) => t.jenis_kebutuhan).filter(Boolean))],
    [source]
  );

  const filteredData = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return source.filter((t) => {
      const pool = [
        t.deskripsi,
        t.kategori_pengeluaran,
        t.jenis_kebutuhan,
        t.tanggal,
        String(t.nominal),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const matchSearch = !q || pool.includes(q);
      const matchCategory = filterCategory === "Semua" || t.kategori_pengeluaran === filterCategory;
      const matchJenis = filterJenis === "Semua" || t.jenis_kebutuhan === filterJenis;
      return matchSearch && matchCategory && matchJenis;
    });
  }, [searchTerm, source, filterCategory, filterJenis]);

  const indexOfLast = currentPage * entries;
  const indexOfFirst = indexOfLast - entries;
  const currentData = filteredData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / entries));

  useEffect(() => setCurrentPage(1), [searchTerm, entries, source.length, filterCategory, filterJenis]);

  const headers = ["Tanggal", "Deskripsi", "Nominal", "Kategori Pengeluaran", "Jenis Kebutuhan"];

  const Table = (
    <>
      {/* Controls */}
      <div className="table-controls">
        <div className="entries-control">
          Show
          <select
            aria-label="Jumlah baris per halaman"
            value={entries}
            onChange={(e) => setEntries(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          entries
        </div>

        {/* Dropdown Filters */}
        <div className="filter-controls">
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            {uniqueCategories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          <select value={filterJenis} onChange={(e) => setFilterJenis(e.target.value)}>
            {uniqueJenis.map((jenis) => (
              <option key={jenis} value={jenis}>
                {jenis}
              </option>
            ))}
          </select>
        </div>

        <div className="search-control">
          <FaSearch className="search-icon" aria-hidden="true" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Cari transaksi"
          />
        </div>
      </div>

      {/* Scrollable Table */}
      <div className="table-scroll" role="region" aria-label="Tabel transaksi" tabIndex={0}>
        <table>
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr>
                <td colSpan="5" className="no-data" style={{ color: "red" }}>
                  {error}
                </td>
              </tr>
            ) : currentData.length > 0 ? (
              currentData.map((t) => (
                <tr key={t._id}>
                  <td>{t.tanggal}</td>
                  <td>{t.deskripsi}</td>
                  <td>{idr(t.nominal)}</td>
                  <td>{t.kategori_pengeluaran}</td>
                  <td>{t.jenis_kebutuhan}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="no-data">
                  Tidak ada data ditemukan
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination" role="navigation" aria-label="Pagination">
        <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
          Sebelum
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            className={currentPage === n ? "active" : ""}
            onClick={() => setCurrentPage(n)}
          >
            {n}
          </button>
        ))}
        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
          Selanjutnya
        </button>
      </div>
    </>
  );

  if (standalone) {
    return (
      <div className="transaction-page">
        <Sidebar />
        <div className="transaction-content">
          <div className="breadcrumb">Halaman / Dasbor</div>
          <h2>Transaksi</h2>
          {Table}
        </div>
      </div>
    );
  }

  return <div className="transaction-table-embed">{Table}</div>;
}
